import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RestaurantRawMaterialCategoryDTO } from '../../../types/restaurant/RestaurantRawMaterialCategoryTypes';
import { RestaurantRawMaterialDTO } from '../../../types/restaurant/RestaurantRawMaterialTypes';
import {
  RestaurantInventoryDTO,
  RestaurantInventoryForm,
} from '../../../types/restaurant/RestaurantInventoryTypes';
import { PageMetadata } from '../../../types/restaurant/PagedResponse';

export const emptyInventoryForm: RestaurantInventoryForm = {
  rawMaterialId: '',
  currentStock: 0,
  minimumStock: 0,
  maximumStock: '',
  averageCost: 0,
  lastPurchaseCost: 0,
};

interface RestaurantInventoryState {
  list: RestaurantInventoryDTO[];
  page: PageMetadata | null;
  rawMaterials: RestaurantRawMaterialDTO[];
  categories: RestaurantRawMaterialCategoryDTO[];
  existingRawMaterialIds: number[];
  form: RestaurantInventoryForm;
  loading: boolean;
  saving: boolean;
  modalOpen: boolean;
}

const initialState: RestaurantInventoryState = {
  list: [],
  page: null,
  rawMaterials: [],
  categories: [],
  existingRawMaterialIds: [],
  form: emptyInventoryForm,
  loading: false,
  saving: false,
  modalOpen: false,
};

const slice = createSlice({
  name: 'restaurantInventory',
  initialState,
  reducers: {
    setRestaurantInventory: (state, action: PayloadAction<{ content: RestaurantInventoryDTO[]; page: PageMetadata }>) => {
      state.list = action.payload.content;
      state.page = action.payload.page;
    },
    setRestaurantInventoryOptions: (
      state,
      action: PayloadAction<{
        rawMaterials: RestaurantRawMaterialDTO[];
        categories: RestaurantRawMaterialCategoryDTO[];
        existingRawMaterialIds: number[];
      }>,
    ) => {
      state.rawMaterials = action.payload.rawMaterials;
      state.categories = action.payload.categories;
      state.existingRawMaterialIds = action.payload.existingRawMaterialIds;
    },
    setRestaurantInventoryLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setRestaurantInventorySaving: (state, action: PayloadAction<boolean>) => {
      state.saving = action.payload;
    },
    openRestaurantInventoryCreate: (state) => {
      state.form = { ...emptyInventoryForm };
      state.modalOpen = true;
    },
    openRestaurantInventoryEdit: (state, action: PayloadAction<RestaurantInventoryDTO>) => {
      const item = action.payload;
      state.form = {
        id: item.id,
        rawMaterialId: item.rawMaterialId,
        currentStock: item.currentStock,
        minimumStock: item.minimumStock,
        maximumStock: item.maximumStock ?? '',
        averageCost: item.averageCost,
        lastPurchaseCost: item.lastPurchaseCost,
      };
      state.modalOpen = true;
    },
    closeRestaurantInventoryModal: (state) => {
      state.form = { ...emptyInventoryForm };
      state.modalOpen = false;
    },
    updateRestaurantInventoryField: (
      state,
      action: PayloadAction<{ field: keyof RestaurantInventoryForm; value: string | number }>,
    ) => {
      state.form = { ...state.form, [action.payload.field]: action.payload.value };
    },
  },
});

export const {
  setRestaurantInventory,
  setRestaurantInventoryOptions,
  setRestaurantInventoryLoading,
  setRestaurantInventorySaving,
  openRestaurantInventoryCreate,
  openRestaurantInventoryEdit,
  closeRestaurantInventoryModal,
  updateRestaurantInventoryField,
} = slice.actions;

export default slice.reducer;
